import { useEffect, useMemo, useState } from "react";
import { Badge, BlockStack, Box, Button, ButtonGroup, Card, InlineStack, Link, Text } from "@shopify/polaris";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "@shopify/polaris-icons";
import { getRequest } from "../../../shared/components/functions";
import { PageSpinner } from "../../../shared/components/spinner/PageSpinner";
import "./fulfilmentCalendar.css";

const MACHINE_GREG_PRODUCT_ID = 10956;
const MACHINE_JACK_PRODUCT_ID = 10957;

const CATEGORY_ORDER = ["machine_greg", "machine_jack", "pitchers", "spare_parts"];
const CATEGORY_DETAILS = {
  machine_greg: {
    label: "Greg machines",
    shortLabel: "Greg",
    color: "#0f8f5a",
    background: "#e7f5ee",
    tone: "success",
  },
  machine_jack: {
    label: "Jack machines",
    shortLabel: "Jack",
    color: "#6d5dfc",
    background: "#efedff",
    tone: "info",
  },
  pitchers: {
    label: "Pitchers",
    shortLabel: "Pitchers",
    color: "#b76e00",
    background: "#fff2d6",
    tone: "attention",
  },
  spare_parts: {
    label: "Spare parts",
    shortLabel: "Parts",
    color: "#1f6feb",
    background: "#eaf2ff",
    tone: "new",
  },
};

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const getDateKeyFromDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getStartOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const parseDateKey = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const normalizeDateKey = (value) => {
  if (!value || String(value).trim().toLowerCase() === "unknown") return null;
  const stringValue = String(value).trim();
  const dateOnlyMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return getDateKeyFromDate(parsedDate);
};

const formatMonthTitle = (date) =>
  new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
  }).format(date);

const formatDateTitle = (dateKey) => {
  const date = parseDateKey(dateKey);
  if (!date) return "No date selected";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const formatQuantity = (value) =>
  new Intl.NumberFormat("nl-BE", {
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const parseList = (value) => {
  if (!value) return [];
  return Array.from(
    new Set(
      String(value)
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
    )
  );
};

const getFulfilmentCategory = (item) => {
  const schuilPartId = Number(item.schuil_part_id);
  if (schuilPartId === MACHINE_GREG_PRODUCT_ID) return "machine_greg";
  if (schuilPartId === MACHINE_JACK_PRODUCT_ID) return "machine_jack";

  const productName = `${item.schuil_product_name || ""} ${item.aha_product_name || ""}`.toLowerCase();
  if (productName.includes("pitcher") || productName.includes("jug") || productName.includes("kan")) return "pitchers";
  return "spare_parts";
};

const buildMonthDays = (monthDate) => {
  const firstOfMonth = getStartOfMonth(monthDate);
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
  const firstCalendarDay = new Date(firstOfMonth);
  firstCalendarDay.setDate(firstCalendarDay.getDate() - mondayOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstCalendarDay);
    date.setDate(firstCalendarDay.getDate() + index);
    return {
      date,
      dateKey: getDateKeyFromDate(date),
      isCurrentMonth: date.getMonth() === monthDate.getMonth() && date.getFullYear() === monthDate.getFullYear(),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
    };
  });
};

const getCategorySummary = (items) =>
  CATEGORY_ORDER.map((category) => {
    const categoryItems = items.filter((item) => item.category === category);
    const quantity = categoryItems.reduce((sum, item) => sum + item.toFulfill, 0);
    return {
      category,
      count: categoryItems.length,
      quantity,
    };
  }).filter((summary) => summary.quantity > 0);

const getCategoryStyle = (category) => ({
  "--fulfilment-category-color": CATEGORY_DETAILS[category].color,
  "--fulfilment-category-bg": CATEGORY_DETAILS[category].background,
});

const getDisplayName = (item) => {
  if (item.category === "machine_greg") return "Greg machine";
  if (item.category === "machine_jack") return "Jack machine";
  return item.aha_product_name && item.aha_product_name !== "translation_error" ? item.aha_product_name : item.schuil_product_name || "Unknown product";
};

const getMonthDateRange = (monthDate) => {
  const start = getDateKeyFromDate(getStartOfMonth(monthDate));
  const end = getDateKeyFromDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));
  return { start, end };
};

export default function FulfilmentCalendar() {
  const todayKey = useMemo(() => getDateKeyFromDate(new Date()), []);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(getStartOfMonth(new Date()));
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey);

  useEffect(() => {
    let active = true;

    async function fetchData() {
      const data = await getRequest("/api/fulfilment/calendar");
      if (!active) return;
      setItems(Array.isArray(data) ? data : []);
      setLoaded(true);
    }

    fetchData();

    return () => {
      active = false;
    };
  }, []);

  const calendarItems = useMemo(
    () =>
      items
        .map((item, index) => {
          const category = getFulfilmentCategory(item);
          const orderIds = parseList(item.order_ids);
          const orderNumbers = parseList(item.order_numbers);

          return {
            ...item,
            id: `${normalizeDateKey(item.due_date) || "unscheduled"}-${item.schuil_part_id || item.aha_part_id || index}-${index}`,
            category,
            dateKey: normalizeDateKey(item.due_date),
            displayName: getDisplayName({ ...item, category }),
            toFulfill: Math.max(Number(item.to_fulfill) || 0, 0),
            orderIds,
            orderNumbers,
            customerNames: parseList(item.customer_names),
            customerReferences: parseList(item.customer_references),
            configStrings: parseList(item.config_strings),
            orderCount: Number(item.order_count) || orderIds.length || orderNumbers.length || 0,
          };
        })
        .sort((a, b) => {
          const categoryDiff = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
          if (categoryDiff !== 0) return categoryDiff;
          return a.displayName.localeCompare(b.displayName);
        }),
    [items]
  );

  const itemsByDate = useMemo(() => {
    const grouped = new Map();
    calendarItems.forEach((item) => {
      if (!item.dateKey) return;
      const existing = grouped.get(item.dateKey) || [];
      grouped.set(item.dateKey, [...existing, item]);
    });
    return grouped;
  }, [calendarItems]);

  const monthDays = useMemo(() => buildMonthDays(calendarMonth), [calendarMonth]);
  const selectedDayItems = itemsByDate.get(selectedDateKey) || [];
  const unscheduledItems = calendarItems.filter((item) => !item.dateKey);
  const { start: monthStart, end: monthEnd } = getMonthDateRange(calendarMonth);
  const visibleMonthItems = calendarItems.filter((item) => item.dateKey && item.dateKey >= monthStart && item.dateKey <= monthEnd);
  const monthSummary = getCategorySummary(visibleMonthItems);
  const selectedDaySummary = getCategorySummary(selectedDayItems);
  const monthOpenQuantity = visibleMonthItems.reduce((sum, item) => sum + item.toFulfill, 0);
  const overdueQuantity = calendarItems.filter((item) => item.dateKey && item.dateKey < todayKey).reduce((sum, item) => sum + item.toFulfill, 0);

  const handleMonthChange = (monthOffset) => {
    const nextMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + monthOffset, 1);
    setCalendarMonth(nextMonth);
    setSelectedDateKey(getDateKeyFromDate(nextMonth));
  };

  const handleToday = () => {
    const today = new Date();
    setCalendarMonth(getStartOfMonth(today));
    setSelectedDateKey(todayKey);
  };

  const handleDaySelect = (day) => {
    setSelectedDateKey(day.dateKey);
    if (!day.isCurrentMonth) setCalendarMonth(getStartOfMonth(day.date));
  };

  if (!loaded) return <PageSpinner />;

  return (
    <Card>
      <Box padding="400">
        <BlockStack gap="400">
          <div className="fulfilment-calendar__toolbar">
            <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
              <InlineStack blockAlign="center" gap="300" wrap>
                <ButtonGroup variant="segmented">
                  <Button icon={ChevronLeftIcon} accessibilityLabel="Previous month" onClick={() => handleMonthChange(-1)} />
                  <Button icon={CalendarIcon} onClick={handleToday}>
                    Today
                  </Button>
                  <Button icon={ChevronRightIcon} accessibilityLabel="Next month" onClick={() => handleMonthChange(1)} />
                </ButtonGroup>
                <BlockStack gap="050">
                  <Text variant="headingLg" as="h2">
                    {formatMonthTitle(calendarMonth)}
                  </Text>
                  <Text as="p" tone="subdued">
                    {formatQuantity(monthOpenQuantity)} open this month
                    {overdueQuantity > 0 ? `, ${formatQuantity(overdueQuantity)} overdue` : ""}
                    {unscheduledItems.length > 0 ? `, ${unscheduledItems.length} without date` : ""}
                  </Text>
                </BlockStack>
              </InlineStack>

              <div className="fulfilment-calendar__legend">
                {CATEGORY_ORDER.map((category) => (
                  <span className="fulfilment-calendar__legend-item" key={category}>
                    <span className="fulfilment-calendar__legend-dot" style={getCategoryStyle(category)} />
                    {CATEGORY_DETAILS[category].label}
                  </span>
                ))}
              </div>
            </InlineStack>

            {monthSummary.length > 0 ? (
              <div className="fulfilment-calendar__month-summary">
                {monthSummary.map((summary) => (
                  <span className="fulfilment-calendar__summary-pill" key={summary.category} style={getCategoryStyle(summary.category)}>
                    {CATEGORY_DETAILS[summary.category].shortLabel}
                    <strong>{formatQuantity(summary.quantity)}</strong>
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="fulfilment-calendar__workspace">
            <div className="fulfilment-calendar__grid-wrap">
              <div className="fulfilment-calendar__grid" role="grid" aria-label={`${formatMonthTitle(calendarMonth)} fulfilment calendar`}>
                {WEEKDAY_LABELS.map((weekday) => (
                  <div className="fulfilment-calendar__weekday" key={weekday} role="columnheader">
                    {weekday}
                  </div>
                ))}
                {monthDays.map((day) => {
                  const dayItems = itemsByDate.get(day.dateKey) || [];
                  const daySummary = getCategorySummary(dayItems);
                  const isSelected = selectedDateKey === day.dateKey;
                  const isToday = todayKey === day.dateKey;

                  return (
                    <button
                      className={[
                        "fulfilment-calendar__day",
                        day.isCurrentMonth ? "" : "fulfilment-calendar__day--outside",
                        day.isWeekend ? "fulfilment-calendar__day--weekend" : "",
                        isSelected ? "fulfilment-calendar__day--selected" : "",
                        isToday ? "fulfilment-calendar__day--today" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      key={day.dateKey}
                      type="button"
                      role="gridcell"
                      aria-selected={isSelected}
                      onClick={() => handleDaySelect(day)}>
                      <span className="fulfilment-calendar__day-number">{day.date.getDate()}</span>
                      <span className="fulfilment-calendar__events">
                        {daySummary.map((summary) => (
                          <span className="fulfilment-calendar__event-chip" key={summary.category} style={getCategoryStyle(summary.category)}>
                            <span>{CATEGORY_DETAILS[summary.category].shortLabel}</span>
                            <strong>{formatQuantity(summary.quantity)}</strong>
                          </span>
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <aside className="fulfilment-calendar__details" aria-label="Selected day fulfilment">
              <BlockStack gap="300">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h3">
                    {formatDateTitle(selectedDateKey)}
                  </Text>
                  <Text as="p" tone="subdued">
                    {selectedDayItems.length > 0 ? `${selectedDayItems.length} fulfilment line${selectedDayItems.length === 1 ? "" : "s"}` : "No fulfilment planned"}
                  </Text>
                </BlockStack>

                {selectedDaySummary.length > 0 ? (
                  <div className="fulfilment-calendar__day-summary">
                    {selectedDaySummary.map((summary) => (
                      <span className="fulfilment-calendar__summary-pill" key={summary.category} style={getCategoryStyle(summary.category)}>
                        {CATEGORY_DETAILS[summary.category].shortLabel}
                        <strong>{formatQuantity(summary.quantity)}</strong>
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="fulfilment-calendar__detail-list">
                  {selectedDayItems.length > 0 ? (
                    selectedDayItems.map((item) => (
                      <div className="fulfilment-calendar__detail-item" key={item.id} style={getCategoryStyle(item.category)}>
                        <InlineStack align="space-between" blockAlign="start" gap="200" wrap={false}>
                          <BlockStack gap="100">
                            <span className="fulfilment-calendar__category-label">{CATEGORY_DETAILS[item.category].label}</span>
                            <Text variant="headingSm" as="h4">
                              {item.displayName}
                            </Text>
                          </BlockStack>
                          <Badge tone={CATEGORY_DETAILS[item.category].tone}>{`x${formatQuantity(item.toFulfill)}`}</Badge>
                        </InlineStack>

                        {item.configStrings.length > 0 ? <div className="fulfilment-calendar__meta">{item.configStrings.join(", ")}</div> : null}

                        <div className="fulfilment-calendar__meta">
                          {item.orderIds.length > 0 && item.orderNumbers.length > 0 ? (
                            item.orderIds.slice(0, 3).map((orderId, index) => (
                              <Link key={`${item.id}-${orderId}`} removeUnderline url={`/sales/${orderId}`}>
                                {`#${item.orderNumbers[index] || orderId}`}
                              </Link>
                            ))
                          ) : (
                            <span>{item.orderCount > 0 ? `${item.orderCount} order${item.orderCount === 1 ? "" : "s"}` : "Order details unavailable"}</span>
                          )}
                          {item.orderIds.length > 3 ? <span>{`+${item.orderIds.length - 3} more`}</span> : null}
                        </div>

                        {item.customerNames.length > 0 || item.customerReferences.length > 0 ? (
                          <div className="fulfilment-calendar__meta fulfilment-calendar__meta--muted">
                            {[...item.customerNames.slice(0, 2), ...item.customerReferences.slice(0, 1)].join(" | ")}
                          </div>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div className="fulfilment-calendar__empty-state">
                      <Text as="p" tone="subdued">
                        No fulfilment planned for this day.
                      </Text>
                    </div>
                  )}
                </div>

                {unscheduledItems.length > 0 ? (
                  <div className="fulfilment-calendar__unscheduled">
                    <InlineStack align="space-between" blockAlign="center" gap="200">
                      <Text variant="headingSm" as="h4">
                        Without date
                      </Text>
                      <Badge tone="critical">{unscheduledItems.length}</Badge>
                    </InlineStack>
                    <div className="fulfilment-calendar__unscheduled-list">
                      {unscheduledItems.slice(0, 4).map((item) => (
                        <span key={item.id}>
                          {CATEGORY_DETAILS[item.category].shortLabel} x{formatQuantity(item.toFulfill)}
                        </span>
                      ))}
                      {unscheduledItems.length > 4 ? <span>{`+${unscheduledItems.length - 4} more`}</span> : null}
                    </div>
                  </div>
                ) : null}
              </BlockStack>
            </aside>
          </div>
        </BlockStack>
      </Box>
    </Card>
  );
}
